import { useCallback, useDeferredValue, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
    clearFilters,
    countActiveFilters,
    parseFilters,
    writeFilter,
    writeFilters,
    writePage,
    writeQuery,
    writeSelecting,
    writeTab,
    writeView,
} from "./section-url-state";

export type SectionView = "card" | "table" | "grid";

export interface UseSectionStateOptions<TFilters extends Record<string, readonly string[]>> {
    defaultTab: string;
    defaultView?: SectionView;
    defaultFilters: TFilters;
}

export interface SectionState<TFilters extends Record<string, readonly string[]>> {
    tab: string;
    view: SectionView;
    query: string;
    deferredQuery: string;
    filters: TFilters;
    selecting: boolean;
    page: number;

    setTab: (tab: string) => void;
    setView: (view: SectionView) => void;
    setQuery: (q: string) => void;
    setFilter: (key: keyof TFilters & string, values: readonly string[]) => void;
    /** Apply several filters in ONE URL update (avoids the last-write-wins clobber of back-to-back setFilter). */
    setFilters: (entries: Partial<Record<keyof TFilters & string, readonly string[]>>) => void;
    toggleFilterValue: (key: keyof TFilters & string, value: string) => void;
    resetFilters: () => void;
    resetAll: () => void;
    setSelecting: (on: boolean) => void;
    setPage: (page: number) => void;
    activeFilterCount: number;
}

/**
 * URL-backed state for SectionShell pages. Defaults are omitted from the URL.
 * Multi-value filters are serialised as CSV (`?status=Dev,Prod`).
 */
export function useSectionState<TFilters extends Record<string, readonly string[]>>(
    options: UseSectionStateOptions<TFilters>,
): SectionState<TFilters> {
    const { defaultTab, defaultView = "card", defaultFilters } = options;
    const [searchParams, setSearchParams] = useSearchParams();

    const tab = searchParams.get("tab") ?? defaultTab;
    const view: SectionView = (searchParams.get("view") as SectionView | null) ?? defaultView;
    const query = searchParams.get("q") ?? "";
    const deferredQuery = useDeferredValue(query);
    const selecting = searchParams.get("select") === "1";
    const page = Number.parseInt(searchParams.get("page") ?? "1", 10) || 1;

    const filters = useMemo<TFilters>(
        () => parseFilters(searchParams, defaultFilters),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultFilters reference is stable enough; URL string drives updates
        [searchParams],
    );

    const updateParams = useCallback(
        (mutator: (next: URLSearchParams) => void) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    mutator(next);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const setTab = useCallback(
        (next: string) => updateParams((p) => writeTab(p, next, defaultTab)),
        [updateParams, defaultTab],
    );

    const setView = useCallback(
        (next: SectionView) => updateParams((p) => writeView(p, next, defaultView)),
        [updateParams, defaultView],
    );

    const setQuery = useCallback((next: string) => updateParams((p) => writeQuery(p, next)), [updateParams]);

    const setFilter = useCallback(
        (key: string, values: readonly string[]) => updateParams((p) => writeFilter(p, key, values)),
        [updateParams],
    );

    const setFilters = useCallback(
        (entries: Partial<Record<keyof TFilters & string, readonly string[]>>) =>
            updateParams((p) => writeFilters(p, entries)),
        [updateParams],
    );

    const toggleFilterValue = useCallback(
        (key: keyof TFilters & string, value: string) => {
            const currentValue: readonly string[] | undefined = filters[key];
            const current = currentValue ?? [];
            const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
            setFilter(key, next);
        },
        [filters, setFilter],
    );

    const resetFilters = useCallback(
        () => updateParams((p) => clearFilters(p, defaultFilters)),
        [updateParams, defaultFilters],
    );

    const resetAll = useCallback(() => {
        setSearchParams(new URLSearchParams(), { replace: true });
    }, [setSearchParams]);

    const setSelecting = useCallback(
        (on: boolean) => updateParams((p) => writeSelecting(p, on)),
        [updateParams],
    );

    const setPage = useCallback((next: number) => updateParams((p) => writePage(p, next)), [updateParams]);

    const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

    return {
        tab,
        view,
        query,
        deferredQuery,
        filters,
        selecting,
        page,
        setTab,
        setView,
        setQuery,
        setFilter,
        setFilters,
        toggleFilterValue,
        resetFilters,
        resetAll,
        setSelecting,
        setPage,
        activeFilterCount,
    };
}
